import {
  composeRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { IconType } from "../../foundations/Icons/types";
import Icon from "../../foundations/Icons/Icon";

export interface ButtonProps extends RACButtonProps {
  /** Name of the icon to display */
  icon: IconType;
  /** Controls if the icon color should change with the color font */
  iconDynamicColor?: boolean;
  variant?: "square" | "round";
  /** Controls the color of the button */
  color?: "tangerine" | "grey" | "black" | "dark" | "transparent";
  /** Controls the size of the button */
  iconClassName?: string;
}

const button = tv({
  slots: {
    base: "group flex items-center justify-center outline-hidden",
    icon: "size-4 transition-all",
  },
  variants: {
    variant: {
      square: "size-6 rounded-2",
      round: {
        base: "size-8 transition-all rounded-full",
        icon: "group-hover:size-5",
      },
    },
    color: {
      transparent: {
        base: "transparent",
        icon: "bg-black",
      },
      dark: {
        base: "bg-black/70",
        icon: "bg-white",
      },
      tangerine: {
        base: "bg-peach",
        icon: "bg-black",
      },
      grey: {
        base: "bg-grey-2",
        icon: "bg-black",
      },
      black: {
        base: "bg-black",
        icon: "bg-white",
      },
    },
  },
  defaultVariants: {
    color: "tangerine",
  },
});

export default function IconButton({
  color,
  icon,
  variant = "round",
  iconDynamicColor = true,
  iconClassName,
  ...props
}: ButtonProps) {
  const { base, icon: iconVariant } = button({ color, variant });
  return (
    <RACButton
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        base({ ...renderProps, className }),
      )}
    >
      <Icon
        className={iconVariant({ className: iconClassName })}
        icon={icon}
        size="md"
        dynamicColor={iconDynamicColor}
      />
    </RACButton>
  );
}
